import type { NpcBench, NpcBenchMember, NpcBenchWrite, NpcCard } from '@matter_extractor/shared';
import {
  NPC_BENCH_NAME_MAX,
  NPC_BENCH_NPCS_MAX,
  npcBenchWriteSchema,
} from '@matter_extractor/shared';

/**
 * What the bench editor holds while somebody types (npc.md §5, H4).
 *
 * A bench is NOT versioned: a name and an ordered list, written whole by one `PUT` - so the draft
 * is that pair and the editor has no autosave, no history and no « Enregistrer une nouvelle
 * version ». The members carry their display facts (name, first name, kind, retired) and not only
 * the identifier, so the list can be drawn without reading the library again, retired NPC included:
 * a retired NPC stays in the bench that carried it and the launch skips it (H24).
 *
 * PURE, written before the component for the reason `protocol-step-draft.ts` gives: the drag, the
 * arrows and the picker all ask for the same list edits, and the second copy would drift.
 */

export interface BenchDraft {
  readonly name: string;
  readonly members: readonly NpcBenchMember[];
}

export const EMPTY_BENCH_DRAFT: BenchDraft = { name: '', members: [] };

export function benchDraftOf(bench: NpcBench | null): BenchDraft {
  return bench === null ? EMPTY_BENCH_DRAFT : { name: bench.name, members: [...bench.npcs] };
}

/** What `PUT` and `POST` take: the trimmed name and the ordered identifiers. */
export function benchWriteOf(draft: BenchDraft): NpcBenchWrite | null {
  const parsed = npcBenchWriteSchema.safeParse({
    name: draft.name,
    npcIds: draft.members.map((member) => member.id),
  });
  return parsed.success ? parsed.data : null;
}

/** Is « Enregistrer » usable: a name, a list the schema accepts, and something changed. */
export function canSaveBench(draft: BenchDraft, saved: NpcBench | null): boolean {
  const write = benchWriteOf(draft);
  if (write === null) return false;
  if (saved === null) return true;
  return (
    write.name !== saved.name ||
    write.npcIds.length !== saved.npcs.length ||
    write.npcIds.some((id, index) => id !== saved.npcs[index]?.id)
  );
}

export function nameLeft(draft: BenchDraft): number {
  return NPC_BENCH_NAME_MAX - draft.name.length;
}

export function canAddMembers(draft: BenchDraft): boolean {
  return draft.members.length < NPC_BENCH_NPCS_MAX;
}

/** One NPC moved by `delta` places; a move off either end changes nothing. */
export function withMemberMoved(draft: BenchDraft, id: string, delta: -1 | 1): BenchDraft {
  const from = draft.members.findIndex((member) => member.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= draft.members.length) return draft;
  return withMemberAt(draft, id, to);
}

/** One NPC put at an index of the list WITHOUT it - what a drop computes. */
export function withMemberAt(draft: BenchDraft, id: string, index: number): BenchDraft {
  const moved = draft.members.find((member) => member.id === id);
  if (moved === undefined) return draft;
  const rest = draft.members.filter((member) => member.id !== id);
  rest.splice(Math.max(0, Math.min(index, rest.length)), 0, moved);
  return { ...draft, members: rest };
}

export function withMemberRemoved(draft: BenchDraft, id: string): BenchDraft {
  return { ...draft, members: draft.members.filter((member) => member.id !== id) };
}

/** The member a card makes, for the picker. */
export function memberOfCard(card: NpcCard): NpcBenchMember {
  return {
    id: card.id,
    name: card.name,
    firstName: card.firstName,
    kind: card.kind,
    retired: card.retiredAt !== null,
  };
}

/** The NPC the picker offers: live ones not already in the bench, by name. */
export function pickableCards(cards: readonly NpcCard[], draft: BenchDraft): NpcCard[] {
  const present = new Set(draft.members.map((member) => member.id));
  return cards
    .filter((card) => card.retiredAt === null && !present.has(card.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
}

/** Members appended in the order given, up to the ceiling of the list. */
export function withMembersAdded(draft: BenchDraft, added: readonly NpcBenchMember[]): BenchDraft {
  const present = new Set(draft.members.map((member) => member.id));
  const next = [...draft.members];
  for (const member of added) {
    if (next.length >= NPC_BENCH_NPCS_MAX) break;
    if (!present.has(member.id)) next.push(member);
  }
  return { ...draft, members: next };
}
